import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  PDFViewer,
} from "@react-pdf/renderer";
import logo from "../../../assets/img/marifer.png";

const styles = StyleSheet.create({
  viewer: {
    width: "100%",
    height: window.innerHeight * 0.75,
  },
  page: {
    padding: 24,
    fontFamily: "Helvetica",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 14,
    borderBottom: "2px solid #f43f5e",
    paddingBottom: 10,
  },
  logoImg: {
    width: 44,
    height: 44,
    marginRight: 12,
  },
  headerTitle: {
    fontSize: 16,
    fontFamily: "Helvetica-Bold",
    color: "#f43f5e",
  },
  headerSub: {
    fontSize: 9,
    color: "#9ca3af",
    marginTop: 2,
  },
  intro: {
    fontSize: 9,
    color: "#374151",
    marginBottom: 12,
    lineHeight: 1.4,
  },
  // Tabla
  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#fce7f3",
    borderRadius: 4,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#fff0f6",
    borderBottomWidth: 1,
    borderBottomColor: "#fce7f3",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#fce7f3",
    minHeight: 46,
    alignItems: "center",
  },
  th: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#be185d",
    padding: 5,
  },
  td: {
    fontSize: 8,
    color: "#374151",
    padding: 5,
  },
  // Anchos de columna
  colEtiqueta: { width: "10%", alignItems: "center" },
  colBarcode: { width: "22%", alignItems: "center" },
  colNombre: { width: "26%" },
  colCodigo: { width: "12%" },
  colDonde: { width: "30%" },
  etiquetaText: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    color: "#000000",
  },
  barcodeImg: {
    width: 90,
    height: 34,
    objectFit: "contain",
  },
  nombreText: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: "#111827",
  },
  subText: {
    fontSize: 7,
    color: "#9ca3af",
    marginTop: 1,
  },
  dondeText: {
    fontSize: 8,
    color: "#374151",
  },
  footer: {
    position: "absolute",
    bottom: 16,
    left: 24,
    right: 24,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTop: "1px solid #fce7f3",
    paddingTop: 6,
  },
  footerText: {
    fontSize: 7,
    color: "#f472b6",
  },
});

// Genera la indicación de dónde pegar la etiqueta a partir de la presentación/unidad
const getDondePegar = (item) => {
  const presentacion = item.details?.presentacion;
  const unidad = item.unit_base;
  if (presentacion) {
    return `Pegar sobre cada ${presentacion}${unidad ? ` (${unidad})` : ""}.`;
  }
  if (unidad) {
    return `Pegar sobre cada unidad (${unidad}).`;
  }
  return "Pegar en un lugar visible del empaque o contenedor del producto.";
};

const getIniciales = (nombre) => {
  if (!nombre) return "";
  return nombre
    .trim()
    .split(/\s+/)
    .map((p) => p.charAt(0).toUpperCase())
    .join("");
};

const GuiaEtiquetasPdf = ({ data }) => {
  if (!data || data.length === 0) return null;

  return (
    <PDFViewer style={styles.viewer}>
      <Document>
        <Page style={styles.page} orientation="landscape" wrap>
          {/* Encabezado */}
          <View style={styles.header} fixed>
            <Image style={styles.logoImg} src={logo} />
            <View>
              <Text style={styles.headerTitle}>
                Guía de Etiquetas — Catálogo de Habilitados
              </Text>
              <Text style={styles.headerSub}>
                Sistema de gestión Marifer · Generado:{" "}
                {new Date().toLocaleDateString("es-MX")} · Total:{" "}
                {data.length} etiquetas
              </Text>
            </View>
          </View>

          <Text style={styles.intro}>
            Esta guía relaciona cada etiqueta (iniciales y código de barras) con
            su producto correspondiente e indica dónde debe pegarse. Busca la
            etiqueta impresa, identifica el producto en esta tabla y colócala en
            el lugar indicado.
          </Text>

          {/* Tabla */}
          <View style={styles.table}>
            {/* Cabecera */}
            <View style={styles.tableHeader} fixed>
              <Text style={[styles.th, styles.colEtiqueta]}>Etiqueta</Text>
              <Text style={[styles.th, styles.colBarcode]}>Código de barras</Text>
              <Text style={[styles.th, styles.colNombre]}>Producto</Text>
              <Text style={[styles.th, styles.colCodigo]}>Código</Text>
              <Text style={[styles.th, styles.colDonde]}>Dónde pegar</Text>
            </View>

            {/* Filas */}
            {data.map((item, index) => (
              <View key={index} style={styles.tableRow} wrap={false}>
                <View style={[styles.td, styles.colEtiqueta]}>
                  <Text style={styles.etiquetaText}>
                    {getIniciales(item.nombre)}
                  </Text>
                </View>
                <View style={[styles.td, styles.colBarcode]}>
                  {!!item.dataURL && (
                    <Image style={styles.barcodeImg} src={item.dataURL} />
                  )}
                </View>
                <View style={[styles.td, styles.colNombre]}>
                  <Text style={styles.nombreText}>{item.nombre}</Text>
                  {!!item.details?.presentacion && (
                    <Text style={styles.subText}>
                      Presentación: {item.details.presentacion}
                    </Text>
                  )}
                  {!!item.descripcion && (
                    <Text style={styles.subText}>{item.descripcion}</Text>
                  )}
                </View>
                <View style={[styles.td, styles.colCodigo]}>
                  <Text>{item.codigo || item.id}</Text>
                </View>
                <View style={[styles.td, styles.colDonde]}>
                  <Text style={styles.dondeText}>{getDondePegar(item)}</Text>
                </View>
              </View>
            ))}
          </View>

          {/* Pie */}
          <View style={styles.footer} fixed>
            <Text style={styles.footerText}>
              ©Marifer {new Date().getFullYear()}
            </Text>
            <Text
              style={styles.footerText}
              render={({ pageNumber, totalPages }) =>
                `Página ${pageNumber} de ${totalPages}`
              }
            />
          </View>
        </Page>
      </Document>
    </PDFViewer>
  );
};

export default GuiaEtiquetasPdf;
